import { useReducer } from 'react';
import { ProductPickerState, SelectionState } from './types';
import { EmbeddedProduct } from 'modules/supplier/products';

type ProductPickerAction =
  | { type: 'SET_PRODUCTS'; allProducts: EmbeddedProduct[] }
  | { type: 'SELECT_VENDOR'; id: number }
  | { type: 'SELECT_OUTLET'; id: number }
  | { type: 'SELECT_SERVICE_CATEGORY'; id: number }
  | { type: 'SELECT_PRODUCT'; id: number };

function setProducts(allProducts: EmbeddedProduct[]): ProductPickerState {
  const vendorMap = new Map();
  const serviceCategoriesMap = new Map();
  let selection: SelectionState | null = null;
  let products: EmbeddedProduct[] = [];
  let outlets: ProductPickerState['outlets'] = [];

  allProducts.forEach((product) => {
    const oosc = product.outlet_or_service_category;

    if (oosc.kind === 'OUTLET') {
      const vendor = oosc.vendor.id;
      vendorMap.set(vendor, oosc.vendor);

      if (!selection) {
        let outlet = null;
        ({ outlets, outlet } = filterOutlets(allProducts, vendor));
        products = filterProducts(allProducts, outlet);

        selection = {
          kind_: 'vendor',
          vendor,
          outlet,
        };
      }
    } else {
      const service_category = oosc.id;
      serviceCategoriesMap.set(oosc.id, oosc);
      if (!selection) {
        products = filterProducts(allProducts, service_category);

        selection = {
          kind_: 'service',
          service_category,
        };
      }
    }
  });
  return {
    selection,
    allProducts,
    vendors: Array.from(vendorMap.values()),
    serviceCategories: Array.from(serviceCategoriesMap.values()),
    outlets,
    products,
  };
}

function filterOutlets(
  allProducts: EmbeddedProduct[],
  vendorId: number | null,
): {
  outlets: ProductPickerState['outlets'];
  outlet: number | null;
} {
  if (!vendorId) {
    return { outlets: [], outlet: null };
  }
  const outletMap = new Map();
  allProducts.forEach((product) => {
    const outlet = product.outlet_or_service_category;
    if (outlet.vendor?.id === vendorId) {
      outletMap.set(outlet.id, outlet);
    }
  });
  const outlets = Array.from(outletMap.values());
  return { outlets, outlet: outlets.length > 0 ? outlets[0].id : null };
}

function filterProducts(allProducts: EmbeddedProduct[], selectedOutletOrCategory: number | null): EmbeddedProduct[] {
  return selectedOutletOrCategory
    ? allProducts.filter((p) => p.outlet_or_service_category.id === selectedOutletOrCategory)
    : [];
}

function productPickerReducer(state: ProductPickerState, action: ProductPickerAction): ProductPickerState {
  switch (action.type) {
    case 'SET_PRODUCTS': {
      return setProducts(action.allProducts);
    }
    case 'SELECT_VENDOR': {
      const { outlets, outlet } = filterOutlets(state.allProducts, action.id);
      const products = filterProducts(state.allProducts, outlet);

      return {
        ...state,
        selection: {
          kind_: 'vendor' as const,
          vendor: action.id,
          outlet,
        },
        outlets,
        products,
      };
    }

    case 'SELECT_SERVICE_CATEGORY': {
      const products = filterProducts(state.allProducts, action.id);

      return {
        ...state,
        selection: {
          kind_: 'service' as const,
          service_category: action.id,
        },
        outlets: [],
        products,
      };
    }

    case 'SELECT_OUTLET': {
      if (state.selection?.kind_ !== 'vendor') return state;
      const products = filterProducts(state.allProducts, action.id);

      return {
        ...state,
        selection: {
          ...state.selection,
          outlet: action.id,
        },
        products,
      };
    }

    default:
      return state;
  }
}

const initialState = {
  allProducts: [],
  selection: null,
  vendors: [],
  serviceCategories: [],
  outlets: [],
  products: [],
};

export function useProductPickerReducer() {
  return useReducer(productPickerReducer, initialState);
}
